import { useState, useEffect } from 'react';
import { X, Check, ChevronLeft } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WA_NUMBER = '918919174876';

const minDate = (() => { const d = new Date(); d.setDate(d.getDate() + 3); return d; })();

function formatDateDisplay(d) {
  if (!d) return '';
  return d.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDateISO(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function StepDot({ label, index, current, total }) {
  const isComplete = index < current || current === total - 1;
  const isActive = index === current;
  return (
    <div style={{ display: 'flex', alignItems: 'center', flex: index < total - 1 ? '1' : 'none' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
        <div style={{
          width: '32px', height: '32px', borderRadius: '50%',
          backgroundColor: isActive || isComplete ? '#5C2424' : 'rgba(92,36,36,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.3s ease',
        }}>
          {isComplete && index < current ? (
            <Check size={14} color="#FAF5EF" strokeWidth={2.5} />
          ) : (
            <span style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '13px', color: isActive || isComplete ? '#FAF5EF' : '#9E8E7E' }}>
              {index + 1}
            </span>
          )}
        </div>
        <span style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: isActive ? '#5C2424' : '#9E8E7E' }}>
          {label}
        </span>
      </div>
      {index < total - 1 && (
        <div style={{ flex: 1, height: '1.5px', backgroundColor: index < current ? '#5C2424' : 'rgba(92,36,36,0.15)', margin: '0 8px', marginBottom: '18px', transition: 'background-color 0.3s ease' }} />
      )}
    </div>
  );
}

export default function BookingModal({ isOpen, onClose }) {
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => { setStep(0); setDate(null); setLocation(''); setBookingId(null); }, 350);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const waLink = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(`Hi! I'd like to book Miso Mini. My event is on ${formatDateDisplay(date)} at ${location}. Please confirm my booking.`)}`;

  const handleConfirm = async () => {
    if (!location.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: formatDateISO(date), location: location.trim(), service: 'Miso Mini' }),
      });
      if (res.ok) {
        const data = await res.json();
        setBookingId(data.id);
      }
    } catch (_) {}
    setLoading(false);
    setStep(2);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop" />

      {/* Modal card */}
      <div className="booking-modal-card" data-testid="booking-modal">
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
          <span style={{ fontFamily: 'Canela, serif', fontStyle: 'italic', fontWeight: 500, fontSize: '22px', color: '#C9A84C', letterSpacing: '2px' }}>
            miso
          </span>
          <button onClick={onClose} data-testid="modal-close-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9E8E7E', display: 'flex', padding: '4px' }} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '32px' }}>
          {['Date', 'Location', 'Confirmed'].map((label, i) => (
            <StepDot key={label} label={label} index={i} current={step} total={3} />
          ))}
        </div>

        {/* ── Step 0: Date ── */}
        {step === 0 && (
          <div data-testid="modal-step-date">
            <h2 style={{ fontFamily: 'Canela, serif', fontWeight: 500, fontSize: 'clamp(24px, 4vw, 32px)', color: '#2D2018', marginBottom: '6px', letterSpacing: '-0.01em' }}>
              When is your event?
            </h2>
            <p style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '14px', color: '#9E8E7E', marginBottom: '20px' }}>
              Choose your event date below.
            </p>
            <div style={{ borderRadius: '12px', border: '1px solid rgba(92,36,36,0.12)', backgroundColor: '#F5EEE8', padding: '4px', marginBottom: '20px' }}>
              <DayPicker
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={{ before: minDate }}
                fromDate={minDate}
              />
            </div>
            {date && (
              <div style={{ backgroundColor: 'rgba(92,36,36,0.07)', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '14px', color: '#2D2018' }}>
                Selected: <strong>{formatDateDisplay(date)}</strong>
              </div>
            )}
            <button
              onClick={() => setStep(1)}
              disabled={!date}
              data-testid="modal-date-next-btn"
              className="btn-primary"
              style={{ width: '100%', textAlign: 'center', opacity: date ? 1 : 0.4, cursor: date ? 'pointer' : 'not-allowed', padding: '15px' }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* ── Step 1: Location ── */}
        {step === 1 && (
          <div data-testid="modal-step-location">
            <button onClick={() => setStep(0)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#9E8E7E', fontFamily: 'Graphik, sans-serif', fontWeight: 700, fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '20px', padding: 0 }}>
              <ChevronLeft size={14} /> Back
            </button>
            <h2 style={{ fontFamily: 'Canela, serif', fontWeight: 500, fontSize: 'clamp(24px, 4vw, 32px)', color: '#2D2018', marginBottom: '6px', letterSpacing: '-0.01em' }}>
              Where is your event?
            </h2>
            <p style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '14px', color: '#9E8E7E', marginBottom: '24px' }}>
              Tell us your city or venue name.
            </p>
            <div style={{ backgroundColor: 'rgba(92,36,36,0.06)', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '14px', color: '#6B5D50' }}>
              Event date: <strong style={{ color: '#2D2018' }}>{formatDateDisplay(date)}</strong>
            </div>
            <input
              type="text"
              placeholder="e.g. The Leela Hotel, Banjara Hills"
              value={location}
              onChange={e => setLocation(e.target.value)}
              data-testid="modal-location-input"
              style={{
                width: '100%', padding: '14px 16px', marginBottom: '20px',
                fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '15px', color: '#2D2018',
                backgroundColor: '#F5EEE8',
                border: '1.5px solid rgba(92,36,36,0.15)',
                borderRadius: '10px', outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={e => e.target.style.borderColor = '#5C2424'}
              onBlur={e => e.target.style.borderColor = 'rgba(92,36,36,0.15)'}
            />
            <button
              onClick={handleConfirm}
              disabled={!location.trim() || loading}
              data-testid="modal-confirm-btn"
              className="btn-primary"
              style={{ width: '100%', textAlign: 'center', opacity: location.trim() ? 1 : 0.4, cursor: location.trim() ? 'pointer' : 'not-allowed', padding: '15px' }}
            >
              {loading ? 'Saving…' : 'Confirm Booking →'}
            </button>
          </div>
        )}

        {/* ── Step 2: Confirmation ── */}
        {step === 2 && (
          <div data-testid="modal-step-confirmed">
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'rgba(92,36,36,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
              <Check size={28} color="#5C2424" strokeWidth={2} />
            </div>
            <h2 style={{ fontFamily: 'Canela, serif', fontWeight: 500, fontSize: 'clamp(26px, 4vw, 34px)', color: '#2D2018', marginBottom: '8px', letterSpacing: '-0.01em' }}>
              You&apos;re on the list!
            </h2>
            <p style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '15px', color: '#6B5D50', marginBottom: '28px', lineHeight: 1.6 }}>
              We&apos;ll be in touch within 24 hours. In the meantime, confirm your booking on WhatsApp to lock in your date.
            </p>

            {/* Summary */}
            <div data-testid="booking-summary-card" style={{ backgroundColor: '#F5EEE8', borderRadius: '14px', border: '1px solid rgba(92,36,36,0.12)', padding: '20px 22px', marginBottom: '24px' }}>
              <p style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#9E8E7E', marginBottom: '14px' }}>Booking Summary</p>
              {[
                { label: 'Service', value: 'Miso Mini' },
                { label: 'Date', value: formatDateDisplay(date) },
                { label: 'Location', value: location },
                { label: 'Price', value: '₹18,000 (100% advance to confirm)' },
                ...(bookingId ? [{ label: 'Reference', value: `#${bookingId}` }] : []),
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid rgba(92,36,36,0.08)' }}>
                  <span style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 700, fontSize: '12px', color: '#9E8E7E', textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>{item.label}</span>
                  <span style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontSize: '14px', color: '#2D2018', textAlign: 'right' }}>{item.value}</span>
                </div>
              ))}
            </div>

            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="modal-whatsapp-btn" className="btn-primary" style={{ display: 'block', textAlign: 'center', marginBottom: '12px', padding: '15px' }}>
              Confirm on WhatsApp →
            </a>
            <button onClick={onClose} data-testid="modal-done-btn" className="btn-outline" style={{ width: '100%', textAlign: 'center', padding: '13px' }}>
              Done
            </button>
          </div>
        )}
      </div>
    </>
  );
}
